<template>
  <r-ai-page-container :current-step="1">
    <v-row v-if="!isUploading && !errorState">
      <!-- PR-FRONT-DOOR-FIX: welcome hero moved to Career Profile empty
           state (that's where new users actually land first). AIUpload
           is mid-action — by the time the user is here, they've already
           chosen to upload. No need to re-orient them. -->
      <v-col cols="12" class="pb-4">
        <h2 class="pb-4">Upload your Resume</h2>
        <div class="text-body-1">
          Upload a PDF, DOCX, or image of your resume. We'll automatically
          extract your information. If we can't read it, we'll guide you to
          enter it manually.
        </div>
        <div
          v-if="hasExistingResume"
          class="text-body-1 error--text font-weight-medium pt-4"
        >
          You have existing resume data. Uploading a new resume will replace
          your existing data.
        </div>
      </v-col>
      <v-col cols="12">
        <!-- PR-FIX-UPLOAD: single click target. Previously the drop zone
             wrapped a Vuetify v-file-input with @click.stop on the inner
             div, so clicks on the v-file-input chrome (filename region,
             empty area around the paperclip) were swallowed silently —
             "1 in 3 clicks did nothing" was the observed symptom. Now
             the entire drop zone is one click target that triggers a
             plain hidden native input. Filename of the picked file is
             shown in a small line below the box instead of inside it. -->
        <div
          class="upload-prompt d-flex flex-column justify-center align-center mb-4"
          :class="{ 'drag-over': isDragging }"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
          @click="triggerFileInput"
        >
          <v-icon class="pt-4" x-large>
            mdi-tray-arrow-up
          </v-icon>
          <div class="pb-2">
            Drag your resume here or click to upload
          </div>
          <div class="text--secondary">
            Supported formats: PDF, DOCX, PNG, JPG, TXT. Max 10MB.
          </div>
          <input
            ref="fileInput"
            type="file"
            style="display: none;"
            accept=".docx,.pdf,.jpg,.jpeg,.png,.txt"
            @change="onFileSelected"
          />
        </div>
        <!-- Selected-file affordance. Lives BELOW the drop zone so it
             can't confuse the click target. Removable so users can pick
             a different file without re-opening the picker first. -->
        <div
          v-if="selectedFile"
          class="d-flex align-center pa-2 px-3 mb-3 selected-file-row"
        >
          <v-icon small color="primary" class="mr-2">mdi-paperclip</v-icon>
          <span class="text-body-2 flex-grow-1" style="word-break: break-all;">
            {{ selectedFile.name }}
          </span>
          <v-btn
            icon
            x-small
            @click="clearSelectedFile"
            aria-label="Remove selected file"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div>
          <v-btn
            color="primary"
            @click="uploadFile()"
            :disabled="!selectedFile || isUploading"
            block
          >
            Upload and Continue
          </v-btn>
        </div>
        <!-- PR-COACH (Block 3-B): "no resume? start blank" fallback link.
             Demoted manual entry — small, secondary, beneath the primary
             CTA. Lets users without a resume on hand still proceed without
             feeling like the product blocked them. Goes to the same
             manual-fill route the error path uses. -->
        <div class="text-center mt-3">
          <a
            href="#"
            class="text-caption text--secondary"
            @click.prevent="startBlank()"
          >
            Don't have a resume? Start from a blank slate
          </a>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="isUploading" class="">
      <!-- PR-AGENT-UX: redesigned loading experience. Three vertical zones:
           (1) thin progress bar at the top, (2) phase narration with icon
           that crossfades between phases, (3) rotating "Did you know" cards
           that educate while the user waits. Both employee-side (capture
           your career story) and candidate-side (apply with confidence)
           audiences are addressed — copy is intentionally agnostic. -->
      <v-col cols="12" class="upload-loading-zone">
        <!-- PR-PROGRESS-LONGTAIL: once we hit the high plateau (>=92%) and the
             request is still in flight, flip to an indeterminate bar so it
             keeps animating instead of parking on a number that reads as
             broken. The determinate bar still drives the normal climb. -->
        <v-progress-linear
          :value="uploadProgress"
          :indeterminate="longRunning && uploadProgress >= 92"
          color="primary"
          height="6"
          rounded
          striped
          class="mb-8"
        ></v-progress-linear>

        <!-- PR-PROGRESS-LONGTAIL: honest, time-aware copy for the long tail so
             the wait reads as "still working," not stuck. -->
        <div
          v-if="longRunning"
          class="text-body-2 text--secondary text-center pb-2 parse-longtail"
        >
          Large resumes can take up to 2-3 minutes - still working...
        </div>

        <transition name="phase-fade" mode="out-in">
          <div class="phase-card text-center" :key="currentPhase.label">
            <v-icon size="56" :color="currentPhase.color" class="pb-2">
              {{ currentPhase.icon }}
            </v-icon>
            <h2 class="pb-1">{{ currentPhase.label }}</h2>
            <div class="text-body-2 text--secondary pb-2">
              {{ currentPhase.subtitle }}
            </div>
          </div>
        </transition>

        <!-- PR-LAUNCH-POLISH: set expectations up front so the wait feels
             intentional, not stuck. Stays put across phase crossfades. -->
        <div class="text-body-2 text--secondary text-center pb-8 parse-reassure">
          This can take up to 3 minutes. We are reading your full history,
          not just skimming it.
        </div>

        <transition name="tip-fade" mode="out-in">
          <div class="tip-card" :key="currentTip.text">
            <div class="tip-header text-caption font-weight-bold">
              <v-icon x-small color="#8B5CF6" class="mr-1"
                >mdi-lightbulb-on-outline</v-icon
              >
              {{ currentTip.header }}
            </div>
            <div class="tip-body text-body-2">
              {{ currentTip.text }}
            </div>
          </div>
        </transition>

        <!-- PR-PARSER-V2.1: gentle "still working" reassurance for the 30-90s
             window where the backend is doing real work but the user might
             start wondering if it's stuck. Replaces the bare seconds counter
             with friendly copy during the long-tail; the counter still shows
             at very long durations (>90s) for power users who want to know. -->
        <div
          v-if="uploadElapsedSeconds >= 30 && uploadElapsedSeconds < 90"
          class="text-caption text--secondary text-center mt-4"
        >
          <v-icon x-small class="mr-1">mdi-coffee-outline</v-icon>
          Still working — large resumes can take a moment.
        </div>
        <div
          v-if="uploadElapsedSeconds >= 90"
          class="text-caption text--secondary text-center mt-4"
        >
          <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
          Almost there — wrapping up your data.
        </div>
        <div
          v-if="uploadElapsedSeconds > 3"
          class="text-caption text--disabled text-right mt-2"
        >
          {{ uploadElapsedSeconds }}s
        </div>
      </v-col>
    </v-row>
    <v-row v-if="errorState">
      <v-col cols="12">
        <v-icon
          class="pt-4"
          :color="isTimeoutError ? 'info' : 'warning'"
          size="72"
        >
          {{
            isTimeoutError
              ? "mdi-clock-alert-outline"
              : "mdi-alert-circle-outline"
          }}
        </v-icon>
        <h2 class="pb-4 pt-2">{{ errorTitle }}</h2>
        <div class="text-body-1 pb-2">
          {{ errorMessage }}
        </div>
        <div class="text-body-2 text--secondary pb-4">
          {{ errorHint }}
        </div>
        <!-- PR-PARSER-V2: correlation ID surfaced so the user can quote it
             when reporting the issue. Only shown when the backend returned
             one (timeout errors from the frontend's own race don't have one). -->
        <div
          v-if="correlationId"
          class="text-caption text--secondary pb-4"
          style="font-family: monospace; opacity: 0.65"
        >
          Reference: {{ correlationId }}
        </div>
        <v-row class="pt-4">
          <v-col cols="12" sm="6">
            <v-btn outlined @click="backToUpload()" class="ai-outlined-btn">
              Try a Different File
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6">
            <v-btn outlined @click="manualFill()" class="ai-outlined-btn">
              Fill in Resume Manually
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </r-ai-page-container>
</template>

<script>
import { UploadResume, GetParseStatus } from "@/api/AIApi.js";
import { mapFields } from "vuex-map-fields";
import { mapState } from "vuex";

// @ is an alias to /src
export default {
  name: "AIUpload",
  data: () => ({
    filename: "",
    selectedFile: null,
    isUploading: false,
    isDragging: false,
    errorState: false,
    isTimeoutError: false,
    backendErrorMsg: "",
    correlationId: "",
    uploadProgress: 0,
    uploadElapsedSeconds: 0,
    uploadStatusText: "Uploading file...",
    progressInterval: null,
    // PR-PROGRESS-LONGTAIL: set once elapsed time crosses ~60s while the
    // request is still pending. Drives the indeterminate-bar fallback and
    // the time-aware reassurance copy so the high plateau never reads as
    // frozen on large resumes (parse can run 2-3 minutes).
    longRunning: false,
    // PR-AGENT-UX: tip rotation state. The tip carousel advances every
    // 6 seconds independent of the phase narration so users get a steady
    // drip of "why stick around" content during the parse.
    tipIndex: 0,
    tipInterval: null
  }),
  computed: {
    // PR-AGENT-UX: phase narration. Tuned to the typical V2 parse curve
    // (a 1–3 page resume completes in ~5–10s; a 10-page resume takes ~25s).
    // Phases advance by elapsed-time bucketing — by the time we show
    // "Capturing your work history," the model genuinely is doing that.
    currentPhase() {
      var s = this.uploadElapsedSeconds;
      if (s < 3) {
        return {
          icon: "mdi-upload-outline",
          label: "Uploading your resume…",
          subtitle: "Sending the file securely",
          color: "#6366F1"
        };
      }
      if (s < 8) {
        return {
          icon: "mdi-text-box-search-outline",
          label: "Reading your resume…",
          subtitle: "Looking at every page",
          color: "#6366F1"
        };
      }
      if (s < 16) {
        return {
          icon: "mdi-file-tree-outline",
          label: "Recognizing your career structure…",
          subtitle: "Sections, dates, and how your story fits together",
          color: "#8B5CF6"
        };
      }
      if (s < 26) {
        return {
          icon: "mdi-briefcase-outline",
          label: "Capturing your work history…",
          subtitle:
            "Companies, roles, and achievements — your words, preserved",
          color: "#8B5CF6"
        };
      }
      if (s < 40) {
        return {
          icon: "mdi-school-outline",
          label: "Extracting skills, credentials, and education…",
          subtitle: "From the document and inferred only when obvious",
          color: "#7C3AED"
        };
      }
      return {
        icon: "mdi-content-save-check-outline",
        label: "Saving your career story…",
        subtitle: "Almost there",
        color: "#10B981"
      };
    },
    currentTip() {
      var tips = this.onboardingTips;
      if (!tips.length) return { header: "", text: "" };
      return tips[this.tipIndex % tips.length];
    },
    // PR-AGENT-UX: 14 rotating tips. Audience-agnostic by design — works
    // for an employee curating their career story OR a candidate
    // preparing to apply. Anchored on (1) product differentiators,
    // (2) what becomes possible once captured, (3) story/coaching
    // wisdom, (4) the "you don't have to do this in one go" reassurance.
    //
    // PR-IDENTITY-TIP: the first tip is the identity statement — what
    // Resumation actually IS, framed as a category move (process, not
    // tool). Always shown first because startUpload() initializes
    // tipIndex to 0 (not random) so every user sees this on first
    // upload regardless of timing.
    onboardingTips() {
      return [
        {
          header: "What Resumation is",
          text:
            "Resumation is a process, not just a tool. Your career story isn't something you write once — it's something you keep building. We just make it the simplest place to do that consistently."
        },
        {
          header: "Why Resumation",
          text:
            "Resumation profiles are evidence-validated. Every claim ties back to a real moment in your career — so your strongest stories rise to the top."
        },
        {
          header: "Quick reassurance",
          text:
            "You don't have to finish everything today. Save your progress and pick up wherever you left off — Sage can help you fill in details later."
        },
        {
          header: "What you can do",
          text:
            "Once your story is captured, you can tailor it to any specific role in under a minute — the data stays intact, the framing adapts."
        },
        {
          header: "How we tell stories",
          text:
            "We use STAR (Situation, Task, Action, Result) to turn 'managed a team' into '$2M cost-save across a team of 12.' We'll walk you through your strongest ones in your voice."
        },
        {
          header: "Privacy first",
          text:
            "Your data is yours. Nothing leaves your profile without your explicit consent — not to employers, not to recruiters, not to anyone."
        },
        {
          header: "Meet Sage",
          text:
            "Sage is your AI career coach. Sage will help you tell your authentic story — not write it for you. Lean on Sage when you're stuck on what to say next."
        },
        {
          header: "How matching works",
          text:
            "Resumation uses deterministic scoring. Same data, same question, same answer — every time. No black-box AI guessing."
        },
        {
          header: "Stand out for what you've done",
          text:
            "Every accomplishment becomes searchable. People find you because of what you've actually accomplished, not your keyword game."
        },
        {
          header: "Built for revision",
          text:
            "Career stories evolve. Add a win this month, refine a role next month — your Resumation profile keeps up with the real you."
        },
        {
          header: "Skill momentum",
          text:
            "Track which skills you're actively building vs the ones going dormant. Your career trajectory becomes visible."
        },
        {
          header: "Capture small wins",
          text:
            "Small wins — a kind word from a manager, a workshop you ran — boost your profile's strength. Add them anytime."
        },
        {
          header: "Cover letters get easy",
          text:
            "Once your story is in Resumation, drafting a cover letter for any role takes seconds. Your wins do the talking."
        },
        {
          header: "Prep for the interview",
          text:
            "Resumation can generate interview questions for any role you're considering — and remind you which of your past wins answer them best."
        },
        {
          header: "Your career, your control",
          text:
            "You can edit, refine, or delete anything anytime. This is your story — we just help you tell it well."
        }
      ];
    },
    // PR-AGENT-UX: previous-block error-rendering computed properties
    // continue below (mapFields, mapState, errorTitle, etc.) — merged from
    // the duplicate `computed:` block that originally lived after methods.
    ...mapFields(["resumeList", "resumeId"]),
    ...mapState("hardSkill", { hardSkills: "hardSkills" }),
    ...mapState("softSkill", { softSkills: "softSkills" }),
    ...mapState("work", { workDict: "workDict" }),
    ...mapState("finalInfo", { displayName: "name" }),
    hasExistingResume() {
      const hasSkills =
        (this.hardSkills && this.hardSkills.length > 0) ||
        (this.softSkills && this.softSkills.length > 0);
      const hasWork = this.workDict && Object.keys(this.workDict).length > 0;
      const hasName = this.displayName && this.displayName.trim().length > 0;
      return hasSkills || hasWork || hasName;
    },
    errorTitle() {
      if (this.isTimeoutError) {
        return "Taking Longer Than Expected";
      }
      return "We Couldn't Read Your Resume";
    },
    errorMessage() {
      if (this.isTimeoutError) {
        return "The upload is taking longer than usual. This can happen with large files or complex documents.";
      }
      if (this.backendErrorMsg) {
        return this.backendErrorMsg;
      }
      return "This sometimes happens with image-based PDFs, scanned documents, or complex formatting.";
    },
    errorHint() {
      if (this.isTimeoutError) {
        return "Try uploading a smaller file, or enter your information manually for faster results.";
      }
      return "Don't worry! You can try a different file format, or we can help you enter your information step by step.";
    }
  },
  async mounted() {},
  beforeDestroy() {
    this.clearProgressInterval();
  },
  methods: {
    triggerFileInput() {
      // PR-FIX-UPLOAD: $refs.fileInput is now the native <input type="file">
      // directly (not a v-file-input wrapper), so .click() works in one
      // call with no DOM-querying. The drop zone has ONE click handler,
      // ONE event path — eliminates the "multiple clicks needed" race.
      var input = this.$refs.fileInput;
      if (input) {
        // Reset value first so re-selecting the same file fires `change`.
        // Without this, picking the same filename twice in a row is a
        // no-op because `value` didn't change.
        input.value = "";
        input.click();
      }
    },
    onFileSelected(event) {
      // PR-FIX-UPLOAD: handle the native <input>'s change event. Mirrors
      // what v-file-input was doing internally (setting selectedFile),
      // but with no Vuetify chrome in the click path.
      var files = event && event.target && event.target.files;
      if (files && files.length > 0) {
        this.selectedFile = files[0];
      }
    },
    clearSelectedFile() {
      this.selectedFile = null;
      // Also reset the native input so picking the same filename again
      // re-fires the change handler.
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    // eslint-disable-next-line no-unused-vars
    onDragOver(event) {
      this.isDragging = true;
    },
    // eslint-disable-next-line no-unused-vars
    onDragLeave(event) {
      this.isDragging = false;
    },
    onDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        // Validate file type
        const file = files[0];
        const validTypes = [".docx", ".pdf", ".jpg", ".jpeg", ".png", ".txt"];
        const fileExtension =
          "." +
          file.name
            .split(".")
            .pop()
            .toLowerCase();

        if (validTypes.includes(fileExtension)) {
          this.selectedFile = file;
        } else {
          alert(
            "Unsupported file type. Please upload a PDF, DOCX, PNG, JPG, or TXT."
          );
        }
      }
    },
    async uploadFile() {
      try {
        this.errorState = false;
        this.isTimeoutError = false;
        this.isUploading = true;
        this.uploadProgress = 0;
        this.uploadElapsedSeconds = 0;
        this.longRunning = false;
        this.uploadStatusText = "Uploading file...";
        this.startProgressTracking();

        if (this.selectedFile == null) {
          console.log("No file selected");
          return;
        }
        const formData = new FormData();
        formData.append("file", this.selectedFile, this.selectedFile.name);

        // PR-PARSER-V2.1: frontend timeout originally 105s → 150s.
        // PR-472-FIX (Jeremy review): bumped 150s → 250s so the axios-level
        // 240s timeout (set in AIApi.UploadResume) fires first with a real
        // network error, instead of this UI race winning ahead of the
        // underlying HTTP call. Order of timeouts now:
        //   backend HttpClient ceiling ........ 240s (ResumeParser.cs)
        //   axios.post timeout ................ 240s (AIApi.js)
        //   UI race backstop .................. 250s (this line)
        // The backstop only fires if axios mis-handles its own timeout.
        const uploadPromise = UploadResume(formData);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("TIMEOUT")), 250000)
        );

        let uploadResponse;
        try {
          uploadResponse = await Promise.race([uploadPromise, timeoutPromise]);
        } catch (raceError) {
          if (raceError.message === "TIMEOUT") {
            this.isTimeoutError = true;
            this.errorState = true;
            // PR-FRONT-DOOR-FIX: flag a parse-fail in sessionStorage so a
            // refresh doesn't bounce the user to the CareerProfile welcome
            // chooser. CareerProfile.mounted reads this and redirects back
            // to /AIUpload?retry=1, restoring the user's place.
            try {
              sessionStorage.setItem("aiUploadFailed", "timeout");
            } catch (e) {
              /* private mode / quota — no-op */
            }
            return;
          }
          throw raceError;
        }

        if (!uploadResponse.success) {
          this.backendErrorMsg = uploadResponse.msg || "";
          // PR-PARSER-V2: surface the correlation ID so users can quote it
          // to support. Backend returns it on every error response.
          this.correlationId =
            (uploadResponse.data && uploadResponse.data.correlationId) ||
            uploadResponse.correlationId ||
            "";
          this.errorState = true;
          // PR-FRONT-DOOR-FIX: same flag-on-fail as the timeout branch above.
          try {
            sessionStorage.setItem("aiUploadFailed", "backend");
          } catch (e) {
            /* private mode / quota — no-op */
          }
        } else {
          // PR-FRONT-DOOR-FIX: success — clear any prior failure flag so a
          // future refresh from CareerProfile doesn't re-route to /AIUpload.
          try {
            sessionStorage.removeItem("aiUploadFailed");
          } catch (e) {
            /* no-op */
          }

          // PR-ASYNC-PARSE: when async mode is on, the upload returns
          // { jobId, async: true } immediately (the parse runs off the SWA
          // gateway in the scheduler worker). Poll for completion. When async
          // is OFF, data is the resumeId string — today's synchronous behavior,
          // unchanged.
          const data = uploadResponse.data;
          if (data && typeof data === "object" && data.async && data.jobId) {
            this.uploadStatusText = "Reading your resume...";
            this.uploadProgress = 88;
            await this.pollParseStatus(data.jobId);
            return;
          }

          this.uploadStatusText = "Loading your profile...";
          this.uploadProgress = 90;
          this.resumeId = data;
          await this.$store.dispatch("loadFullResume", this.resumeId);
          this.$router.push({ path: "/AINameAndAddress" });
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        this.errorState = true;
      } finally {
        this.clearProgressInterval();
        this.isUploading = false;
      }
    },
    // PR-ASYNC-PARSE: poll GetParseStatus until the worker finishes. The parse
    // runs off the SWA gateway, so this can wait as long as we allow. The
    // generous ceiling covers a launch-day queue; if exceeded we surface the
    // same friendly "timeout" path (the resume still finishes + saves
    // server-side, so the user can retry or find it on their profile).
    async pollParseStatus(jobId) {
      const POLL_INTERVAL_MS = 2500;
      const MAX_WAIT_MS = 10 * 60 * 1000;
      const start = Date.now();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - start > MAX_WAIT_MS) {
          this.isTimeoutError = true;
          this.errorState = true;
          try {
            sessionStorage.setItem("aiUploadFailed", "timeout");
          } catch (e) {
            /* no-op */
          }
          return;
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

        let statusResp;
        try {
          statusResp = await GetParseStatus(jobId);
        } catch (e) {
          continue; // transient network blip — keep polling
        }
        if (!statusResp || !statusResp.success) {
          continue; // transient backend hiccup — keep polling
        }

        const d = statusResp.data || {};
        if (d.status === "done" && d.resumeId) {
          this.uploadStatusText = "Loading your profile...";
          this.uploadProgress = 95;
          this.resumeId = d.resumeId;
          try {
            sessionStorage.removeItem("aiUploadFailed");
          } catch (e) {
            /* no-op */
          }
          await this.$store.dispatch("loadFullResume", this.resumeId);
          this.$router.push({ path: "/AINameAndAddress" });
          return;
        }
        if (d.status === "failed") {
          this.backendErrorMsg =
            d.displayText || "We couldn't read your resume. Please try again.";
          this.errorState = true;
          try {
            sessionStorage.setItem("aiUploadFailed", "backend");
          } catch (e) {
            /* no-op */
          }
          return;
        }
        // queued / running — nudge the progress bar and keep waiting.
        if (this.uploadProgress < 92) {
          this.uploadProgress += 1;
        }
      }
    },
    startProgressTracking() {
      // PR-AGENT-UX: progress bar advances smoothly across the realistic
      // V2 parse curve. With V2 single-call parsing, typical resumes
      // finish in 5–10s; long-form / academic CVs take ~25–40s. The
      // bar caps high (not 100%) so it doesn't claim "done" before the
      // backend actually returns - the final jump to 100% lands when the
      // upload promise resolves / the poll reports done.
      //
      // PR-PROGRESS-LONGTAIL: the schedule no longer flat-lines at 92% at
      // 60s. Large resumes can parse for 2-3 minutes (up to the 240s
      // timeout), and a determinate bar parked at 92% read as broken.
      // We now keep creeping slowly toward ~97% out to ~3 minutes, and
      // once we cross 60s we flip longRunning on so the display switches
      // to an indeterminate bar plus time-aware copy (see template).
      const statusSteps = [
        { time: 0, progress: 12 },
        { time: 3, progress: 25 },
        { time: 8, progress: 42 },
        { time: 16, progress: 58 },
        { time: 26, progress: 72 },
        { time: 40, progress: 85 },
        { time: 60, progress: 92 },
        { time: 90, progress: 94 },
        { time: 120, progress: 96 },
        { time: 180, progress: 97 }
      ];

      this.progressInterval = setInterval(() => {
        this.uploadElapsedSeconds++;
        const currentStep = [...statusSteps]
          .reverse()
          .find(s => this.uploadElapsedSeconds >= s.time);
        if (currentStep) {
          this.uploadProgress = currentStep.progress;
        }
        // PR-PROGRESS-LONGTAIL: cross into the honest long-tail UX at ~60s.
        if (this.uploadElapsedSeconds >= 60) {
          this.longRunning = true;
        }
      }, 1000);

      // Rotate the "Did you know" tip every 6 seconds. Independent of the
      // phase narration above so users get a steady drip of content.
      //
      // PR-IDENTITY-TIP: start at index 0 deterministically (not random)
      // so the identity tip ("Resumation is a process, not just a tool")
      // is the first thing every user reads on first upload. The
      // randomized start meant most users never saw the identity tip
      // before the parse completed — wrong audience-first-impression.
      this.tipIndex = 0;
      this.tipInterval = setInterval(() => {
        this.tipIndex = (this.tipIndex + 1) % this.onboardingTips.length;
      }, 6000);
    },
    clearProgressInterval() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
      if (this.tipInterval) {
        clearInterval(this.tipInterval);
        this.tipInterval = null;
      }
    },
    backToUpload() {
      this.errorState = false;
      this.isTimeoutError = false;
      this.backendErrorMsg = "";
      this.selectedFile = null;
      this.isUploading = false;
      this.isDragging = false;
      this.uploadProgress = 0;
      this.uploadElapsedSeconds = 0;
      this.longRunning = false;
      // PR-FRONT-DOOR-FIX: clear the failure flag so a retry that succeeds
      // doesn't re-bounce on next CareerProfile load.
      try {
        sessionStorage.removeItem("aiUploadFailed");
      } catch (e) {
        /* no-op */
      }
    },
    manualFill() {
      this.$router.push({ path: "/AINameAndAddress" });
    },
    // PR-COACH (Block 3-B): "Don't have a resume? Start blank" — same
    // destination as manualFill() (the manual entry path), but framed
    // for the user who never had a resume to upload in the first place.
    startBlank() {
      this.$router.push({ path: "/AINameAndAddress" });
    }
  }
};
</script>

<style scoped>
.upload-prompt {
  border: 2px dashed #aaaaaa;
  background-color: #ffffff;
  border-radius: 10px;
  padding: 32px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-prompt.drag-over {
  border-color: #1976d2;
  background-color: #e3f2fd;
  transform: scale(1.02);
}

/* PR-FIX-UPLOAD: selected-file affordance below the drop zone. Soft
   background so it reads as "your pick" without competing with the
   drop zone for attention. */
.selected-file-row {
  background-color: #f5f9ff;
  border: 1px solid #bbdefb;
  border-radius: 8px;
}
/* PR-FIX-DARK-TEXT: the file-name row is always a light chip — pin dark
   text so dark mode (which flips text to white) doesn't make the file
   name invisible against the light background. */
.selected-file-row,
.selected-file-row .text-body-2 {
  color: #2b2f3a !important;
}

/* PR-AGENT-UX: loading experience styling. Three vertical zones —
   progress bar, phase narration card, rotating tip card. Subtle
   transitions, no external animation libraries, no layout shift
   between phases or tips. */
.upload-loading-zone {
  padding-top: 24px;
}
.phase-card {
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.phase-card h2 {
  font-weight: 600;
  letter-spacing: -0.01em;
}
.phase-fade-enter-active,
.phase-fade-leave-active {
  transition: opacity 250ms ease, transform 250ms ease;
}
.phase-fade-enter,
.phase-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
.tip-card {
  background: linear-gradient(135deg, #faf7ff 0%, #f3f0ff 100%);
  border-left: 3px solid #8b5cf6;
  border-radius: 6px;
  padding: 16px 20px;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  max-width: 480px;
  margin: 0 auto;
}
.tip-card .tip-header {
  color: #6b21a8;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.tip-card .tip-body {
  color: #1f2937;
  line-height: 1.5;
}
.tip-fade-enter-active,
.tip-fade-leave-active {
  transition: opacity 400ms ease, transform 400ms ease;
}
.tip-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}
.tip-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
